import { Box } from '@mui/material';
import firebase from 'firebase/compat/app';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendReceipt } from '../../api/receipt-api';
import Modal from '../../components/Modal';
import { CustomTab, CustomTabs } from '../../components/Tabs';
import TabPanel from '../../components/Tabs/TabPanel';
import { LANG_TEXT } from '../../constants/language';
import { useAuthContext } from '../../context/authContext';
import { useNotificationContext } from '../../context/notificationContext';
import { useOrderContext } from '../../context/orderContext';
import { isAllMatch } from '../../utils/multiOrder.utils';
import TabContent from './components/TabContent';
import TabLabel from './components/TabLabel';
import DeliveryInfo from './DeliveryInfo';
import DeliveryModal from './DeliveryInfo/components/DeliveryModal';
import { DamagedItemStatus, ICreateOrder, Order, OrderItem, OrderItemStatus, TabValue } from './order.types';
import PreOrderDrawer from './PreOrderDrawer/PreOrderDrawer';

const Orders = () => {
  const { t } = useTranslation();
  const { user, isOnline } = useAuthContext();
  const {
    orderList,
    deliveredOrders,
    newOrders,
    ongoingOrders,
    preOrders,
    loading,
    seatDataMap,
    handleUpdateOrderStatus,
    updateOrderStatusDamaged,
    restaurantList,
    handleCreateOrder,
    fetchOrderById,
  } = useOrderContext();

  const searchParams = useSearchParams();
  const orderId = searchParams[0].get('id');
  const init = useRef(false);
  const [tab, setTab] = useState(TabValue.NEW_ORDER);
  const [showInfo, setShowInfo] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order>();
  const [open, setOpen] = useState<boolean>(false);
  const [damaged, setDamaged] = useState<Order>({} as Order);
  const [damagedRestaurantId, setDamagedRestaurantId] = useState<string | undefined>();
  const { showToast } = useNotificationContext();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleCloseModal = () => {
    setOpenModal(false);
    setDamagedRestaurantId(undefined);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = () => {
    setOpen(false);
    setShowInfo(false);
    navigate(`/`);
  };

  useEffect(() => {
    if (selectedOrder) {
      const selected = orderList?.find((item) => item.id === selectedOrder.id);
      if (selected && (!selected.rider_id || selected.rider_id === user?.uid)) {
        setSelectedOrder(selected);
      } else {
        if (showInfo) setOpen(true);
      }
    }
  }, [orderList]);

  useEffect(() => {
    if (orderId && orderList) {
      const selected = orderList?.find((item) => item.id === orderId);
      if (selected) {
        setShowInfo(true);

        setSelectedOrder(selected);
        init.current = true;
      }
    }
  }, [orderId, orderList]);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: TabValue) => {
    setTab(newValue);
  };

  const handleShowInfo = (_order: Order) => {
    setShowInfo(true);
    setSelectedOrder(_order);
  };

  const handleCloseInfo = () => {
    setShowInfo(false);
    navigate(`/`);
  };

  const handlePickup = async (order: Order, restaurantIds?: string[]) => {
    if (order.id && user) {
      try {
        const _order = await fetchOrderById(order.id);

        if (!_order || !_order.id) {
          showToast({
            content: t(LANG_TEXT.PICK_UP_ORDER_FAIL_MESSAGE),
            type: 'error',
          });
          return;
        }

        const isMultiple = _order.multi_orders && _order.multi_orders.length > 1;

        if (isMultiple && restaurantIds) {
          const claimableRestaurantIds =
            _order.multi_orders?.filter((item) => !item.rider_id && restaurantIds.includes(item.restaurant_id)).map((item) => item.restaurant_id) || [];

          if (!claimableRestaurantIds.length) {
            showToast({
              content: t(LANG_TEXT.PICK_UP_ORDER_FAIL_MESSAGE),
              type: 'error',
            });
            return;
          }

          await handleUpdateOrderStatus(_order.id, {
            multi_orders: _order.multi_orders?.map((item) => (claimableRestaurantIds.includes(item.restaurant_id) ? { ...item, rider_id: user.uid } : item)),
          });
        } else {
          if (_order.rider_id && _order.rider_id !== user.uid) {
            showToast({
              content: t(LANG_TEXT.PICK_UP_ORDER_FAIL_MESSAGE),
              type: 'error',
            });
            return;
          }

          await handleUpdateOrderStatus(_order.id, {
            rider_id: user.uid,
            multi_orders: [
              {
                rider_id: user.uid,
                status: _order.multi_orders?.[0]?.status || _order?.order_status || OrderItemStatus.PREPARING,
                restaurant_id: _order.restaurant_id,
              },
            ],
          });
        }

        setTab(TabValue.ONGOING);
        showToast({
          content: t(LANG_TEXT.PICK_UP_ORDER_SUCCESS_MESSAGE),
          type: 'success',
        });
      } catch (error) {
        showToast({
          content: t(LANG_TEXT.PICK_UP_ORDER_FAIL_MESSAGE),
          type: 'error',
        });
      }
    }
  };

  const handleConfirmPickedUp = async (order: Order, restaurantIds?: string[]) => {
    if (!order.id || !user) return;

    try {
      const _order = await fetchOrderById(order.id);

      if (!_order || !_order.id) {
        showToast({
          content: t(LANG_TEXT.UPDATE_STATUS_FAIL_MESSAGE),
          type: 'error',
        });
        return;
      }

      const isMultiple = _order.multi_orders && _order.multi_orders.length > 1;
      const updateData: Record<string, any> = {};
      const newStatus = _order?.order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP ? OrderItemStatus.DAMAGED_DELIVERING : OrderItemStatus.DELIVERING;

      if (_order.foods?.length && _order.type) {
        const isMixedStatus =
          (_order.food_order_status === OrderItemStatus.READY_FOR_PICK_UP && _order.food_promotion_order_status === OrderItemStatus.DELIVERING) ||
          (_order.food_order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP && _order.food_promotion_order_status === OrderItemStatus.DAMAGED_DELIVERING) ||
          (_order.food_order_status === OrderItemStatus.DELIVERING && _order.food_promotion_order_status === OrderItemStatus.READY_FOR_PICK_UP) ||
          (_order.food_order_status === OrderItemStatus.DAMAGED_DELIVERING && _order.food_promotion_order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP);

        if (isMixedStatus) {
          if (isMultiple && restaurantIds) {
            updateData.multi_orders = _order.multi_orders?.map((item) =>
              restaurantIds.includes(item.restaurant_id) ? { ...item, status: newStatus, rider_id: user.uid } : item
            );

            updateData.order_status = isAllMatch(updateData.multi_orders, newStatus) ? newStatus : _order.order_status;
          } else {
            updateData.multi_orders = [{ rider_id: user.uid, status: newStatus, restaurant_id: _order.restaurant_id }];
            updateData.order_status = newStatus;
          }
        }

        const foodStatusKey = _order.type === 'promotion' ? 'food_promotion_order_status' : 'food_order_status';
        updateData[foodStatusKey] = newStatus;
      } else {
        if (isMultiple && restaurantIds) {
          updateData.multi_orders = _order.multi_orders?.map((item) =>
            restaurantIds.includes(item.restaurant_id) ? { ...item, status: newStatus, rider_id: user.uid } : item
          );

          updateData.order_status = isAllMatch(updateData.multi_orders, newStatus) ? newStatus : _order.order_status;
        } else {
          updateData.multi_orders = [{ rider_id: user.uid, status: newStatus, restaurant_id: _order.restaurant_id }];
          updateData.order_status = newStatus;
        }
      }

      await handleUpdateOrderStatus(_order.id, updateData);
      if (_order.has_damaged_order) {
        const neededUpdated =
          _order.multi_orders?.filter((item) => (restaurantIds?.length ? restaurantIds.includes(item.restaurant_id) : false)).map((item) => item.restaurant_id) || [];

        await updateOrderStatusDamaged(_order.id, DamagedItemStatus.DAMAGED_DELIVERING, neededUpdated);
      }

      showToast({
        content: t(LANG_TEXT.PICKED_UP_FOOD_SUCCESS_MESSAGE),
        type: 'success',
      });
    } catch (error) {
      showToast({
        content: t(LANG_TEXT.UPDATE_STATUS_FAIL_MESSAGE),
        type: 'error',
      });
    }
  };

  const handleConfirmDelivered = async (order: Order, restaurantIds?: string[]) => {
    if (!order.id || !user) return;

    try {
      const _order = await fetchOrderById(order.id);

      if (!_order || !_order.id) {
        showToast({
          content: t(LANG_TEXT.UPDATE_STATUS_FAIL_MESSAGE),
          type: 'error',
        });
        return;
      }

      const newStatus = OrderItemStatus.DELIVERED;
      const timestamp = firebase.firestore.Timestamp.now();
      const riderId = user.uid;
      const isMultiple = _order.multi_orders && _order.multi_orders.length > 1;

      const updateData: Record<string, any> = {
        order_delivered_time: timestamp,
      };

      if (_order.foods?.length && _order.type) {
        const isMixedStatus =
          (_order.food_order_status === OrderItemStatus.DELIVERING && _order.food_promotion_order_status === OrderItemStatus.DELIVERED) ||
          (_order.food_order_status === OrderItemStatus.DAMAGED_DELIVERING && _order.food_promotion_order_status === OrderItemStatus.DELIVERED) ||
          (_order.food_order_status === OrderItemStatus.DELIVERED && _order.food_promotion_order_status === OrderItemStatus.DELIVERING) ||
          (_order.food_order_status === OrderItemStatus.DELIVERED && _order.food_promotion_order_status === OrderItemStatus.DAMAGED_DELIVERING);

        const foodStatusKey = _order.type === 'promotion' ? 'food_promotion_order_status' : 'food_order_status';
        updateData[foodStatusKey] = newStatus;

        if (isMixedStatus) {
          if (isMultiple && restaurantIds) {
            updateData.multi_orders = _order.multi_orders?.map((item) =>
              restaurantIds.includes(item.restaurant_id) ? { ...item, status: newStatus, rider_id: riderId } : item
            );

            updateData.order_status = isAllMatch(updateData.multi_orders, newStatus) ? newStatus : _order.order_status;
            updateData.completed_time = isAllMatch(updateData.multi_orders, newStatus) ? timestamp : '';
          } else {
            updateData.multi_orders = [{ rider_id: user.uid, status: newStatus, restaurant_id: _order.restaurant_id }];
            updateData.order_status = newStatus;
            updateData.completed_time = timestamp;
          }
        }
      } else {
        if (isMultiple && restaurantIds) {
          updateData.multi_orders = _order.multi_orders?.map((item) =>
            restaurantIds.includes(item.restaurant_id) ? { ...item, status: newStatus, rider_id: riderId } : item
          );
          updateData.order_status = isAllMatch(updateData.multi_orders, newStatus) ? newStatus : _order.order_status;
          updateData.completed_time = isAllMatch(updateData.multi_orders, newStatus) ? timestamp : '';
        } else {
          updateData.multi_orders = [{ rider_id: user.uid, status: newStatus, restaurant_id: _order.restaurant_id }];
          updateData.order_status = newStatus;
          updateData.completed_time = timestamp;
        }
      }

      await handleUpdateOrderStatus(_order.id, updateData);

      if (updateData.order_status === newStatus) {
        await sendReceipt(_order.id, 'success');
      }
      if (_order.has_damaged_order) {
        const neededUpdated =
          _order.multi_orders?.filter((item) => (restaurantIds?.length ? restaurantIds.includes(item.restaurant_id) : false)).map((item) => item.restaurant_id) || [];

        await updateOrderStatusDamaged(_order.id, DamagedItemStatus.DAMAGED_DELIVERED, neededUpdated);
      }

      setTab(TabValue.DELIVERED);
      showToast({
        content: t(LANG_TEXT.DELIVERED_SUCCESS_MESSAGE),
        type: 'success',
      });
    } catch (error) {
      showToast({
        content: t(LANG_TEXT.UPDATE_STATUS_FAIL_MESSAGE),
        type: 'error',
      });
    }
  };

  const handleOpenDamageModal = (item: Order, restaurantId?: string) => {
    setDamaged(item);
    setDamagedRestaurantId(restaurantId);
    setOpenModal(true);
  };

  const updateOrderData = (order: Order, restaurantId?: string): ICreateOrder => {
    const filterFoodsByRestaurant = (foods: OrderItem[]) => {
      if (!restaurantId) return foods;
      return foods.filter((food) => food.restaurantId === restaurantId);
    };

    const filteredFoods = filterFoodsByRestaurant(order.foods || []).map((food) => {
      const { item_deleted_quantity, ...rest } = food;
      rest.item_quantity = item_deleted_quantity !== undefined ? rest.item_quantity - item_deleted_quantity : rest.item_quantity;
      return rest;
    });

    const filteredPromotionFoods = filterFoodsByRestaurant(order.promotion_foods || []).map((food) => {
      const { item_deleted_quantity, ...rest } = food;
      rest.item_quantity = item_deleted_quantity !== undefined ? rest.item_quantity - item_deleted_quantity : rest.item_quantity;
      return rest;
    });

    const damagedOrderData: ICreateOrder = {
      bill_status: order.bill_status || '',
      foods: filteredFoods,
      order_create_time: firebase.firestore.Timestamp.fromDate(new Date()),
      order_status: DamagedItemStatus.DAMAGED_CREATED,
      order_table_number: order.order_table_number || '',
      ordered_by: order.ordered_by || '',
      payment_method: order.payment_method || '',
      promotion_foods: filteredPromotionFoods,
      restaurant_id: restaurantId || order.restaurant_id || '',
      seat_number: order.seat_number || '',
      seat_type: order.seat_type || '',
      parent_id: order.id,
      session_id: order.session_id || '',
      is_damaged: true,
      stadium_id: order.stadium_id || '',
      requested_delivery_time: order.requested_delivery_time,
      order_type: order.order_type,
      is_pre_order: order.is_pre_order,
    };

    if (order.rider_id) {
      damagedOrderData.rider_id = order.rider_id;
    }

    return damagedOrderData;
  };

  const createOrder = async (id: string, orderDetails: ICreateOrder) => {
    try {
      await handleCreateOrder(id, orderDetails);
      showToast({
        content: t(LANG_TEXT.PICK_UP_ORDER_SUCCESS_MESSAGE),
        type: 'success',
      });
    } catch (error) {
      showToast({
        content: t(LANG_TEXT.PICK_UP_ORDER_FAIL_MESSAGE),
        type: 'error',
      });
    }
  };

  const handleDamageFood = async (_order: Order, restaurantId?: string) => {
    if (_order.id) {
      try {
        const newOrderData = updateOrderData(_order, restaurantId);
        const damaged_number = _order?.damaged_number ?? 0;
        const id = damaged_number > 0 ? `${_order.id}_DAMAGED_${_order?.damaged_number}` : `${_order.id}_DAMAGED`;
        await createOrder(id, newOrderData);

        const updateData: any = {
          order_status: OrderItemStatus.DAMAGED_PREPARING,
          has_damaged_order: true,
          damaged_number: damaged_number + 1,
        };

        if (_order.multi_orders && _order.multi_orders.length > 0) {
          if (restaurantId) {
            updateData.multi_orders = _order.multi_orders.map((item) =>
              item.restaurant_id === restaurantId
                ? {
                    ...item,
                    status: OrderItemStatus.DAMAGED_PREPARING,
                    rider_id: user?.uid || item.rider_id,
                  }
                : item
            );
          } else {
            updateData.multi_orders = _order.multi_orders.map((item) => ({
              ...item,
              status: OrderItemStatus.DAMAGED_PREPARING,
              rider_id: user?.uid || item.rider_id,
            }));
          }
        } else if (user?.uid) {
          updateData.rider_id = user.uid;
        }

        await handleUpdateOrderStatus(_order.id, updateData, true);

        setOpenModal(false);
        setDamagedRestaurantId(undefined);

        showToast({
          content: t(LANG_TEXT.PICK_UP_ORDER_SUCCESS_MESSAGE),
          type: 'success',
        });
      } catch (error) {
        showToast({
          content: t(LANG_TEXT.PICK_UP_ORDER_FAIL_MESSAGE),
          type: 'error',
        });
      }
    }
  };

  const tabs = useMemo(() => {
    return [
      {
        value: TabValue.NEW_ORDER,
        label: t(LANG_TEXT.TAB_NEW_ORDER),
        totalItems: newOrders?.length,
      },
      {
        value: TabValue.ONGOING,
        label: t(LANG_TEXT.TAB_ONGOING),
        totalItems: ongoingOrders?.length,
      },
      {
        value: TabValue.DELIVERED,
        label: t(LANG_TEXT.TAB_DELIVERED),
        totalItems: deliveredOrders?.length,
      },
    ];
  }, [deliveredOrders?.length, newOrders?.length, ongoingOrders?.length]);

  return (
    <Box height="100%" position="relative" overflow="hidden">
      <Box height="100%">
        <Box padding="16px" bgcolor="#F9FAFB">
          <CustomTabs
            scrollButtons={false}
            variant="scrollable"
            value={tab}
            onChange={(event, newValue) => {
              handleChangeTab(event, newValue);
            }}
          >
            {tabs.map((_tab) => (
              <CustomTab key={_tab.value} label={<TabLabel label={_tab.label} counter={_tab.totalItems} />} value={_tab.value} />
            ))}
          </CustomTabs>
        </Box>
        <Box height="calc(100% - 70px)" overflow="auto" sx={{ scrollBehavior: 'smooth' }}>
          <TabPanel active={tab} value={TabValue.NEW_ORDER}>
            <TabContent
              loading={loading}
              data={newOrders}
              onClickCTA={handlePickup}
              onShowInfo={handleShowInfo}
              emptyStateTitle={t(LANG_TEXT.TAB_NEW_ORDER_EMPTY_STATE)}
              disabled={!isOnline}
              onDamage={handleOpenDamageModal}
            />
          </TabPanel>
          <TabPanel active={tab} value={TabValue.ONGOING}>
            <TabContent
              loading={loading}
              data={ongoingOrders}
              onClickCTA={handleConfirmPickedUp}
              onShowInfo={handleShowInfo}
              emptyStateTitle={t(LANG_TEXT.TAB_ONGOING_EMPTY_STATE)}
              disabled={!isOnline}
              onDamage={handleOpenDamageModal}
              claimed
            />
          </TabPanel>
          <TabPanel active={tab} value={TabValue.DELIVERED}>
            <TabContent
              loading={loading}
              data={deliveredOrders}
              onClickCTA={handleConfirmDelivered}
              onShowInfo={handleShowInfo}
              emptyStateTitle={t(LANG_TEXT.TAB_DELIVERED_EMPTY_STATE)}
              disabled={!isOnline}
              onDamage={handleOpenDamageModal}
              claimed
            />
          </TabPanel>
        </Box>
      </Box>
      <PreOrderDrawer preOrders={preOrders} restaurantList={restaurantList} seatDataMap={seatDataMap} onClick={handlePickup} handleShowInfo={handleShowInfo} />
      <DeliveryInfo
        open={showInfo}
        onClose={handleCloseInfo}
        item={selectedOrder}
        onPickUp={handlePickup}
        onConfirmPickedUp={handleConfirmPickedUp}
        onConfirmDelivered={handleConfirmDelivered}
        seatDataMap={seatDataMap}
        claimed={tab !== TabValue.NEW_ORDER}
      />
      <DeliveryModal open={open} handleClose={handleClose} handleSubmit={handleSubmit} />
      <Modal open={openModal} handleClose={handleCloseModal} handleSubmit={async () => await handleDamageFood(damaged, damagedRestaurantId)} />
    </Box>
  );
};

export default Orders;
